export interface InputState {
  throttle: number; // 0..1
  brake: number; // 0..1
  steer: number; // -1 (left) .. 1 (right)
  gyroRoll: number; // camera roll angle in radians (from device tilt)
  gyroActive: boolean; // true when gyro steering is providing input
}

export function createInput(): InputState {
  const state: InputState = { throttle: 0, brake: 0, steer: 0, gyroRoll: 0, gyroActive: false };

  const keys = new Set<string>();

  window.addEventListener("keydown", (e) => {
    keys.add(e.key.toLowerCase());
    updateFromKeys(state, keys);
  });

  window.addEventListener("keyup", (e) => {
    keys.delete(e.key.toLowerCase());
    updateFromKeys(state, keys);
  });

  // Gamepad polling
  function pollGamepad(): void {
    const gamepads = navigator.getGamepads();
    for (const gp of gamepads) {
      if (!gp) continue;
      // Standard mapping: right trigger = throttle, left trigger = brake
      state.throttle = Math.max(state.throttle, gp.buttons[7]?.value ?? 0);
      state.brake = Math.max(state.brake, gp.buttons[6]?.value ?? 0);
      // Left stick X = steer
      const stickX = gp.axes[0] ?? 0;
      if (Math.abs(stickX) > 0.05) {
        state.steer = stickX;
      }
      break; // use first connected gamepad
    }
    requestAnimationFrame(pollGamepad);
  }
  pollGamepad();

  // Touch controls
  initTouch(state);

  // Gyroscope steering (mobile)
  initGyroscope(state);

  return state;
}

// --- Touch input ---
// Layout: left half = pedals (top = throttle, bottom = brake)
//         right half = steering (horizontal drag) — disabled when gyro active

function initTouch(state: InputState): void {
  // Track active touches by zone
  const touches: {
    pedal: number | null; // touch identifier for pedal zone
    steer: number | null; // touch identifier for steer zone
    steerStartX: number;  // where the steer touch began
  } = { pedal: null, steer: null, steerStartX: 0 };

  // Max horizontal drag distance (pixels) for full lock steering
  const STEER_RANGE = 80;

  function halfW(): number {
    return window.innerWidth / 2;
  }

  function handleTouchStart(e: TouchEvent): void {
    e.preventDefault();
    for (let i = 0; i < e.changedTouches.length; i++) {
      const t = e.changedTouches[i];
      if (state.gyroActive || t.clientX < halfW()) {
        // Pedal zone (full screen when gyro active, left half otherwise)
        touches.pedal = t.identifier;
        applyPedal(state, t.clientY);
      } else {
        // Right half — steer zone (only when gyro is not active)
        touches.steer = t.identifier;
        touches.steerStartX = t.clientX;
        state.steer = 0;
      }
    }
  }

  function handleTouchMove(e: TouchEvent): void {
    e.preventDefault();
    for (let i = 0; i < e.changedTouches.length; i++) {
      const t = e.changedTouches[i];
      if (t.identifier === touches.pedal) {
        applyPedal(state, t.clientY);
      } else if (t.identifier === touches.steer && !state.gyroActive) {
        const dx = t.clientX - touches.steerStartX;
        state.steer = Math.max(-1, Math.min(1, dx / STEER_RANGE));
      }
    }
  }

  function handleTouchEnd(e: TouchEvent): void {
    e.preventDefault();
    for (let i = 0; i < e.changedTouches.length; i++) {
      const t = e.changedTouches[i];
      if (t.identifier === touches.pedal) {
        touches.pedal = null;
        state.throttle = 0;
        state.brake = 0;
      } else if (t.identifier === touches.steer) {
        touches.steer = null;
        if (!state.gyroActive) state.steer = 0;
      }
    }
  }

  const canvas = document.getElementById("game")!;
  canvas.addEventListener("touchstart", handleTouchStart, { passive: false });
  canvas.addEventListener("touchmove", handleTouchMove, { passive: false });
  canvas.addEventListener("touchend", handleTouchEnd, { passive: false });
  canvas.addEventListener("touchcancel", handleTouchEnd, { passive: false });
}

function applyPedal(state: InputState, clientY: number): void {
  const midY = window.innerHeight / 2;
  if (clientY < midY) {
    // Top half — throttle: closer to top = more throttle
    state.throttle = Math.max(0, Math.min(1, 1 - clientY / midY));
    state.brake = 0;
  } else {
    // Bottom half — brake: closer to bottom = more brake
    state.throttle = 0;
    state.brake = Math.max(
      0,
      Math.min(1, (clientY - midY) / (window.innerHeight - midY))
    );
  }
}

// --- Gyroscope steering ---
// Uses DeviceOrientation API to steer by tilting the phone like a steering wheel.
// Camera roll is locked 1:1 to the phone tilt; 90° tilt = full steering lock.

function initGyroscope(state: InputState): void {
  // Only attempt on touch-capable devices
  if (!("ontouchstart" in window)) return;

  function startListening(): void {
    window.addEventListener("deviceorientation", (e: DeviceOrientationEvent) => {
      if (e.gamma == null && e.beta == null) return;

      // Get the tilt angle that represents "steering wheel rotation"
      // accounting for current screen orientation
      const tilt = getSteerTilt(e);

      state.gyroActive = true;
      // 90° of phone tilt = full steering lock (-1 to +1)
      state.steer = Math.max(-1, Math.min(1, tilt / 90));
      // Camera roll matches phone tilt 1:1
      state.gyroRoll = tilt * (Math.PI / 180);

      // Hide touch steer zone label on first gyro event
      const steerZone = document.querySelector(".touch-zone.steer") as HTMLElement | null;
      if (steerZone) steerZone.style.display = "none";
    });
  }

  // iOS 13+ requires explicit permission from a user gesture
  const DOE = DeviceOrientationEvent as unknown as {
    requestPermission?: () => Promise<string>;
  };
  if (typeof DOE.requestPermission === "function") {
    // Request on first touch
    const canvas = document.getElementById("game")!;
    canvas.addEventListener(
      "touchstart",
      () => {
        DOE.requestPermission!().then((result: string) => {
          if (result === "granted") startListening();
        });
      },
      { once: true }
    );
  } else {
    // Android / desktop — just listen
    startListening();
  }
}

function getSteerTilt(e: DeviceOrientationEvent): number {
  const gamma = e.gamma ?? 0;
  const beta = e.beta ?? 0;
  const orientation =
    (screen.orientation?.angle ?? (window as unknown as { orientation?: number }).orientation) ?? 0;

  switch (orientation) {
    case 0:
      return gamma; // portrait
    case 90:
      return -beta; // landscape-left (home button right)
    case -90:
    case 270:
      return beta; // landscape-right (home button left)
    case 180:
      return -gamma; // portrait upside-down
    default:
      return gamma;
  }
}

function updateFromKeys(state: InputState, keys: Set<string>): void {
  state.throttle = keys.has("arrowup") || keys.has("w") ? 1 : 0;
  state.brake = keys.has("arrowdown") || keys.has("s") ? 1 : 0;

  const left = keys.has("arrowleft") || keys.has("a");
  const right = keys.has("arrowright") || keys.has("d");
  if (left && !right) state.steer = -1;
  else if (right && !left) state.steer = 1;
  else state.steer = 0;
}
